import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, ChevronRight, X, Sparkles, Receipt, Globe, Wallet, ShoppingBag } from "lucide-react";

/**
 * 3-step onboarding wizard shown on first login.
 *   Step 1 — Analysis Context (country + currency + business model)
 *   Step 2 — Connect Xero (or skip)
 *   Step 3 — Run first cash-flow forecast (or "Explore the platform")
 *
 * Auto-hidden when all steps already complete, or user dismisses.
 */
const COUNTRIES = [
  { country: "Australia", currency_code: "AUD", currency_symbol: "A$" },
  { country: "Nigeria", currency_code: "NGN", currency_symbol: "₦" },
  { country: "South Africa", currency_code: "ZAR", currency_symbol: "R" },
  { country: "Kenya", currency_code: "KES", currency_symbol: "KSh" },
  { country: "India", currency_code: "INR", currency_symbol: "₹" },
  { country: "Brazil", currency_code: "BRL", currency_symbol: "R$" },
  { country: "United States", currency_code: "USD", currency_symbol: "$" },
  { country: "United Kingdom", currency_code: "GBP", currency_symbol: "£" },
  { country: "European Union", currency_code: "EUR", currency_symbol: "€" },
  { country: "United Arab Emirates", currency_code: "AED", currency_symbol: "د.إ" },
  { country: "Saudi Arabia", currency_code: "SAR", currency_symbol: "﷼" },
];

// Phase 35B — industry list. Mining/Shipping route to dedicated playbooks;
// the rest fall through to country-banking playbooks.
const INDUSTRIES = [
  { value: "Banking", label: "Banking / Financial Services" },
  { value: "Mining", label: "Mining & Resources" },
  { value: "Shipping", label: "Shipping & Maritime" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Retail", label: "Retail & Consumer Goods" },
  { value: "Professional Services", label: "Professional Services" },
  { value: "Technology", label: "Technology / SaaS" },
  { value: "Healthcare", label: "Healthcare" },
  { value: "Real Estate", label: "Real Estate / Construction" },
  { value: "Energy", label: "Energy / Oil & Gas" },
  { value: "Agriculture", label: "Agriculture / Agri-tech" },
  { value: "Other", label: "Other" },
];

export default function OnboardingWizard() {
  const nav = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState({ steps: {} });
  const [ctx, setCtx] = useState({
    country: "Australia", currency_code: "AUD", currency_symbol: "A$",
    business_model: "", target_segments: "",
  });
  const [industry, setIndustry] = useState("Banking");
  const [saving, setSaving] = useState(false);
  const [forecasting, setForecasting] = useState(false);

  const loadStatus = async () => {
    try {
      const r = await api.get("/workspace/onboarding");
      setStatus(r.data);
      // Pre-fill from existing workspace
      try {
        const ws = await api.get("/workspace");
        if (ws.data?.industry) setIndustry(ws.data.industry);
        if (ws.data?.analysis_context) {
          setCtx(prev => ({ ...prev, ...ws.data.analysis_context }));
        }
      } catch { /* non-blocking */ }
      // Show wizard unless user dismissed it OR everything already done
      if (!r.data.dismissed && !r.data.completed) {
        setVisible(true);
        // Resume from first incomplete step
        if (!r.data.steps.context) setStep(1);
        else if (!r.data.steps.xero) setStep(2);
        else setStep(3);
      }
    } catch {
      // Silent — wizard just stays hidden
    }
  };
  useEffect(() => { loadStatus(); }, []);

  if (!visible) return null;

  const close = () => setVisible(false);

  const skip = async () => {
    await api.post("/workspace/onboarding/dismiss").catch(() => {});
    toast.success("Wizard dismissed. Re-enable any time in Settings.");
    close();
  };

  const saveContext = async () => {
    setSaving(true);
    try {
      await api.patch("/workspace", { analysis_context: ctx, industry });
      const inferredPlaybook = (
        industry === "Mining" ? "Mining playbook"
        : industry === "Shipping" ? "Shipping playbook"
        : `${ctx.country} banking playbook`
      );
      toast.success(`Saved — DARA will use the ${inferredPlaybook} for your data.`);
      setStep(2);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  const startXero = async () => {
    try {
      const { data } = await api.get("/connectors/xero/status");
      if (!data.configured) {
        toast.error("Xero isn't configured on this DARA instance yet — your admin needs to set XERO_CLIENT_ID.");
        return;
      }
      const r = await api.get("/connectors/xero/login");
      const url = r.data.authorize_url;
      // Use window.top to break out of any preview iframe
      try {
        if (window.top && window.top !== window.self) { window.top.location.href = url; return; }
      } catch (_) { /* cross-origin frame */ }
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Xero OAuth");
    }
  };

  const runForecast = async () => {
    setForecasting(true);
    try {
      await api.post("/cashflow/forecast");
      toast.success("First forecast ready — opening now.");
      await api.post("/workspace/onboarding/complete");
      close();
      nav("/app/cashflow");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forecast failed — try again from Cash-Flow page.");
    }
    setForecasting(false);
  };

  const finishAndExplore = async () => {
    await api.post("/workspace/onboarding/complete").catch(() => {});
    close();
    nav("/app/");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="onboarding-wizard"
    >
      <div className="w-full max-w-2xl bg-black border border-zinc-800 shadow-2xl">
        {/* header */}
        <div className="flex items-center justify-between border-b border-zinc-900 px-5 py-3">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-[#FFB000]" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">GETTING STARTED</div>
              <div className="font-heading font-black text-base tracking-tight">WELCOME TO DARA</div>
            </div>
          </div>
          <button data-testid="onboarding-close" onClick={skip} className="text-zinc-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* step pills */}
        <div className="flex items-center gap-2 px-5 pt-4">
          {[
            { n: 1, label: "CONTEXT", done: !!status.steps?.context },
            { n: 2, label: "CONNECT XERO", done: !!status.steps?.xero },
            { n: 3, label: "FIRST FORECAST", done: !!status.steps?.forecast },
          ].map(s => (
            <div
              key={s.n}
              className={`flex-1 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest flex items-center justify-center gap-1.5 ${
                s.done ? "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/5"
                  : step === s.n ? "border-[#FFB000]/50 text-[#FFB000] bg-[#FFB000]/5"
                  : "border-zinc-800 text-zinc-600"
              }`}
            >
              {s.done && <Check className="w-3 h-3" />}
              {s.n}. {s.label}
            </div>
          ))}
        </div>

        {/* step body */}
        <div className="p-5 min-h-[260px]">
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Globe className="w-4 h-4 text-[#FFB000] mt-1" />
                <div>
                  <div className="font-heading font-bold text-lg">Tell DARA your market</div>
                  <p className="text-xs text-zinc-500 mt-1 max-w-lg">
                    This picks the right currency for every chart, insight, and forecast.
                    DARA uses this context to cite the right regulators (e.g. ATO, RBI, SARB).
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Country</span>
                  <select
                    data-testid="onboarding-country"
                    value={ctx.country}
                    onChange={(e) => {
                      const pick = COUNTRIES.find(c => c.country === e.target.value) || COUNTRIES[0];
                      setCtx({ ...ctx, ...pick });
                    }}
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  >
                    {COUNTRIES.map(c => <option key={c.country} value={c.country}>{c.country} · {c.currency_code}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Industry</span>
                  <select
                    data-testid="onboarding-industry"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                  >
                    {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                  </select>
                </label>
              </div>
              {/* Soft hint about which playbook this picks */}
              <div className="font-mono text-[10px] text-zinc-500 leading-relaxed border-l-2 border-[#FFB000]/40 pl-3">
                {industry === "Mining" && "→ DARA will use the Mining playbook (JORC Code, mine-call factor, royalty disclosure, IMO 2020)."}
                {industry === "Shipping" && "→ DARA will use the Shipping playbook (BIMCO charter terms, OFAC sanctions screening, IMO 2020 fuel mix)."}
                {industry !== "Mining" && industry !== "Shipping" && (
                  <>→ DARA will use the {ctx.country} banking playbook to score your data against local regulators.</>
                )}
              </div>
              <label className="block">
                <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Business model (optional)</span>
                <input
                  data-testid="onboarding-business-model"
                  value={ctx.business_model}
                  onChange={(e) => setCtx({ ...ctx, business_model: e.target.value })}
                  placeholder="e.g. SMB Advisory, B2B SaaS, Iron-ore exporter"
                  className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                />
              </label>
              <label className="block">
                <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Target segments (comma-separated)</span>
                <input
                  data-testid="onboarding-segments"
                  value={ctx.target_segments}
                  onChange={(e) => setCtx({ ...ctx, target_segments: e.target.value })}
                  placeholder="e.g. AU SMBs, Trades, Professional Services"
                  className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                />
              </label>
              <div className="flex justify-end gap-2 pt-3">
                <button
                  onClick={saveContext}
                  disabled={saving || !ctx.country}
                  data-testid="onboarding-step1-next"
                  className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? "SAVING…" : "NEXT"} <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Receipt className="w-4 h-4 text-[#FFB000] mt-1" />
                <div>
                  <div className="font-heading font-bold text-lg">Connect a data source</div>
                  <p className="text-xs text-zinc-500 mt-1 max-w-lg">
                    Pick the source that fits your business. You can connect more later from the Sources page.
                  </p>
                </div>
              </div>

              {/* Xero option */}
              <div className="border border-zinc-900 bg-zinc-950/40 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-sm flex items-center gap-2">
                      <Receipt className="w-3.5 h-3.5 text-[#FFB000]" />
                      Xero (Accounting)
                    </div>
                    <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
                      Invoices · contacts · chart-of-accounts (read-only). Powers cash-flow forecasting + hygiene playbook.
                      No org? Use Xero's free{" "}
                      <a href="https://my.xero.com/!pbzm/Organisation/DemoCompany" target="_blank" rel="noreferrer" className="text-[#FFB000] underline">Demo Company</a>.
                    </p>
                  </div>
                  <button
                    onClick={startXero}
                    data-testid="onboarding-xero-connect"
                    className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 flex items-center gap-2 flex-shrink-0"
                  >
                    {status.steps?.xero ? "RE-AUTHORIZE" : "CONNECT XERO"}
                  </button>
                </div>
              </div>

              {/* Square POS option — Phase 36.26 */}
              {(industry === "Retail" || ctx.business_model?.toLowerCase().match(/coffee|cafe|restaurant|retail|shop|store|hospitality/)) && (
                <div className="border border-zinc-900 bg-zinc-950/40 p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-sm flex items-center gap-2">
                        <ShoppingBag className="w-3.5 h-3.5 text-[#00E676]" />
                        Square POS (Retail / Hospitality)
                      </div>
                      <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
                        Live transaction feed from your Square terminal — every sale, tip, and refund streams into DARA's
                        POS Live dashboard within seconds. 30-day backfill on first connect.
                      </p>
                    </div>
                    <button
                      onClick={() => { close(); nav("/app/sources"); }}
                      data-testid="onboarding-square-connect"
                      className="border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 font-mono text-[10px] uppercase tracking-widest px-4 py-2 flex items-center gap-2 flex-shrink-0"
                    >
                      OPEN SOURCES →
                    </button>
                  </div>
                </div>
              )}

              <div className="border border-zinc-900 bg-zinc-950/40 p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">SCOPES YOU'LL AUTHORIZE (XERO)</div>
                <ul className="space-y-1 font-mono text-[10px] text-zinc-300">
                  <li>• <span className="text-[#00E676]">accounting.invoices.read</span> · <span className="text-[#00E676]">accounting.contacts.read</span> · <span className="text-[#00E676]">accounting.settings.read</span> · <span className="text-zinc-500">offline_access</span></li>
                </ul>
                <div className="font-mono text-[10px] text-zinc-600 mt-1.5">
                  Zero write scopes — DARA never mutates your data.
                </div>
              </div>

              <div className="flex justify-between gap-2 pt-2">
                <button
                  onClick={() => setStep(3)}
                  data-testid="onboarding-xero-skip"
                  className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white"
                >
                  SKIP FOR NOW
                </button>
                <button
                  onClick={() => setStep(3)}
                  data-testid="onboarding-step2-next"
                  className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
                >
                  CONTINUE <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Wallet className="w-4 h-4 text-[#FFB000] mt-1" />
                <div>
                  <div className="font-heading font-bold text-lg">Run your first forecast</div>
                  <p className="text-xs text-zinc-500 mt-1 max-w-lg">
                    DARA reads your Xero AR ageing and projects 30 / 60 / 90-day cash collection,
                    flags concentration risk, and writes a board-ready narrative in{" "}
                    <span className="text-[#FFB000]">{ctx.currency_symbol}{ctx.currency_code}</span>. Takes ~15 seconds.
                  </p>
                </div>
              </div>
              {!status.steps?.xero && (
                <div className="border border-[#FFB000]/30 bg-[#FFB000]/5 px-3 py-2 font-mono text-[10px] text-[#FFB000]">
                  ⚠ Xero not connected. The forecast needs it — you can still "Explore the platform" and come back later.
                </div>
              )}
              <div className="flex justify-between gap-2 pt-3">
                <button
                  onClick={finishAndExplore}
                  data-testid="onboarding-explore"
                  className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white"
                >
                  EXPLORE THE PLATFORM
                </button>
                <button
                  onClick={runForecast}
                  disabled={forecasting || !status.steps?.xero}
                  data-testid="onboarding-run-forecast"
                  className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:opacity-90 disabled:opacity-40 flex items-center gap-2"
                >
                  {forecasting ? "FORECASTING…" : "RUN FIRST FORECAST →"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
